import { useEffect, useId, useRef } from 'react';

import { token } from '@bp-files/ui';
import type { FileSummary } from '@/lib/api';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FRAME_BUTTON_CLASS,
  ViewerFrame,
} from '@/components/Viewer/ViewerFrame';
import { useFileBlob } from '@/components/Viewer/useFileBlob';
import { VIEWER_COPY } from '@/components/Viewer/viewer.copy';
import { isForeignKey } from '@/components/Viewer/keyboard';
import { IMAGE_VIEWER_COPY } from './imageViewer.copy';
import { ZoomStage, type ZoomStageHandle } from './ZoomStage';

/**
 * The full-screen image viewer (FIL-8a).
 *
 * A photograph, its name, a download, and movement to the folder's other
 * images. **What is left in this file is only what is true of a photograph** —
 * the portal, the focus trap, the header and the overflow menu moved to
 * `Viewer/ViewerFrame` when FIL-8b's document reader needed the same frame, and
 * that component's header carries the reasoning that used to live here.
 *
 * **Stepping is this viewer's, and does not generalise.** A folder's
 * photographs are a set a reviewer compares across, which is why the footer
 * counts them and the arrows move between them. A document is one thing you
 * stay inside; `PdfViewer`'s footer counts pages instead. FIL-8b says so.
 *
 * **Opening this is an audited download** — the same endpoint, the same
 * `file.downloaded` row, the same metered bytes (UPL-8). `useFileBlob` has the
 * detail; FIL-8a says it in the spec, and ADR-0053 records why there is no
 * second read path.
 */

/** What `+` and `-` are worth. A step a reader can aim with, not a jump. */
const KEY_ZOOM_STEP = 1.4;

export interface ImageViewerProps {
  /** The image on screen. Its `id` is what the bytes are fetched by. */
  file: FileSummary;
  /** Where it sits in the folder's viewable images, zero-based. */
  index: number;
  total: number;
  onStep: (delta: number) => void;
  onJump: (index: number) => void;
  onClose: () => void;
  /** Runs the row's ordinary download — the same act, from inside the frame. */
  onDownload: () => void;
  downloadLabel: string;
  downloadBusy: boolean;
}

export function ImageViewer({
  file,
  index,
  total,
  onStep,
  onJump,
  onClose,
  onDownload,
  downloadLabel,
  downloadBusy,
}: ImageViewerProps) {
  const titleId = useId();
  const stageRef = useRef<ZoomStageHandle>(null);

  const { state, retry } = useFileBlob(file.id, file.sizeBytes);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      /*
        Arrows navigate whatever the zoom is, and a step returns to fit.

        The reference implementation disables navigation while zoomed and pans
        with the arrows instead — which makes one key mean two things depending
        on state nothing on screen shows, and a reviewer comparing the same
        corner of two photographs meets it every time. One meaning per key; the
        reset is the honest consequence.

        Escape is not here: `ViewerFrame` owns it, because closing is the one
        act that means the same thing in every viewer.
      */
      // Nothing on this screen takes typed input today, and the guard is here
      // anyway: this handler is the one `PdfViewer` was copied from, where the
      // missing check ate three characters out of every signing field.
      if (isForeignKey(event)) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onStep(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          onStep(1);
          break;
        case 'Home':
          event.preventDefault();
          onJump(0);
          break;
        case 'End':
          event.preventDefault();
          onJump(total - 1);
          break;
        case '+':
        case '=':
          event.preventDefault();
          stageRef.current?.zoomBy(KEY_ZOOM_STEP);
          break;
        case '-':
          event.preventDefault();
          stageRef.current?.zoomBy(1 / KEY_ZOOM_STEP);
          break;
        case '0':
          event.preventDefault();
          stageRef.current?.reset();
          break;
        default:
          break;
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onJump, onStep, total]);

  return (
    <ViewerFrame
      title={file.name}
      titleId={titleId}
      onClose={onClose}
      menuItems={
        <button
          type="button"
          disabled={downloadBusy}
          onClick={onDownload}
          className="flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-elevated disabled:opacity-60"
        >
          {downloadLabel}
        </button>
      }
      footer={
        <>
          <button
            type="button"
            onClick={() => {
              onStep(-1);
            }}
            disabled={index <= 0}
            aria-label={IMAGE_VIEWER_COPY.previous}
            className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body`}
          >
            <ChevronLeftIcon />
          </button>

          <p
            aria-live="polite"
            className="text-xs tabular-nums"
            style={{ color: token('bp-text-secondary') }}
          >
            {IMAGE_VIEWER_COPY.position(index + 1, total)}
          </p>

          <button
            type="button"
            onClick={() => {
              onStep(1);
            }}
            disabled={index >= total - 1}
            aria-label={IMAGE_VIEWER_COPY.next}
            className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body`}
          >
            <ChevronRightIcon />
          </button>
        </>
      }
    >
      {state.kind === 'ready' ? (
        // Keyed by the file, so stepping remounts the stage and returns it to
        // fit — see `ZoomStage`'s own note on why that is the reset mechanism.
        <ZoomStage key={file.id} ref={stageRef} src={state.src} alt={file.name} />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4">
          {state.kind === 'error' ? (
            <>
              <p className="text-center text-sm" style={{ color: token('bp-text-secondary') }}>
                {state.message}
              </p>
              <button
                type="button"
                onClick={retry}
                className="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-elevated"
                style={{ borderColor: token('bp-border') }}
              >
                {VIEWER_COPY.errors.retry}
              </button>
            </>
          ) : (
            <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
              {state.kind === 'loading' && state.totalBytes > 0
                ? VIEWER_COPY.loadingProgress(
                    Math.min(100, Math.floor((state.receivedBytes / state.totalBytes) * 100)),
                  )
                : VIEWER_COPY.loading}
            </p>
          )}
        </div>
      )}
    </ViewerFrame>
  );
}
